import { ContentService } from '@atlas/shared/data-access/content';
import type { IApiConfig } from '@atlas/shared/data-access/shared';
import { merge, pick } from 'lodash-es';
import { type AppCountry, type AppLanguage, type PerCountryLanguageMap } from '../models/index.ts';
import type { TranslationSchema } from '../models/TranslationSchema.ts';
import { getSupportedLanguages, isAutoTranslatedLanguage, isSupportedLanguage } from './language.ts';
import { cleanCmsScopeKey, cleanCmsTranslations, copyDeeplyObject } from './utils.ts';

/* eslint-disable @typescript-eslint/no-explicit-any */

export type BaseTranslationsLoaderConfig<Language = AppLanguage> = {
    country: AppCountry;
    language: Language;
    scopes: string[];
    apiConfig: IApiConfig;
};

type GetLanguageTranslationsOptions = {
    fetchServerTranslations?: boolean;
    mergeWithFallbacks?: boolean;
};

export abstract class BaseTranslationsLoader<Schema extends object> {
    protected abstract getFallbackTranslationsMaps(): PerCountryLanguageMap<() => Promise<Schema>>;

    constructor(public config: BaseTranslationsLoaderConfig) {}

    async getLanguageTranslations({
        fetchServerTranslations = true,
        mergeWithFallbacks = true
    }: GetLanguageTranslationsOptions = {}) {
        const { country, language } = this.config;
        let result = await this.getFallbacks();
        if (fetchServerTranslations) {
            try {
                const serverTranslations = await this.getServerTranslations();
                result = mergeWithFallbacks ? merge(result, serverTranslations) : serverTranslations;
            } catch (error) {
                console.warn('Unable to retrieve translations from server');
                console.warn(error);
                if (isAutoTranslatedLanguage(country, language)) {
                    throw error;
                }
            }
        }

        return result;
    }

    private async getFallbacks() {
        const { country, scopes } = this.config;
        let { language } = this.config;

        if (!isSupportedLanguage(country, language)) {
            console.warn(`Language not configured for this country : ${country}/${language}`);
        }

        // For auto-translated languages, use the default language for fallbacks
        if (isAutoTranslatedLanguage(country, language)) {
            language = getSupportedLanguages(country)[0];
        }

        const fallbackTranslationsMap = this.getFallbackTranslationsMaps();
        if (
            fallbackTranslationsMap[country] &&
            (language as keyof (typeof fallbackTranslationsMap)[typeof country]) in fallbackTranslationsMap[country]
        ) {
            const langMap = fallbackTranslationsMap[country] as Record<string, () => Promise<Schema>>;
            const fallbacks = await langMap[language]();

            let result = pick(
                fallbacks,
                scopes.map((scope) => cleanCmsScopeKey(scope))
            );
            result = copyDeeplyObject(result);
            return result;
        } else {
            throw new Error(`Translations fallbacks for ${language} not found.`);
        }
    }

    private async getServerTranslations() {
        const { scopes, apiConfig } = this.config;

        const qs = await import('qs');
        const serverTranslations = await new ContentService().contentTranslationGet(
            { scopes },
            {
                ...apiConfig,
                paramsSerializer: {
                    serialize: (params: Record<string, any>) => {
                        return qs.stringify(params);
                    }
                }
            }
        );
        return cleanCmsTranslations(serverTranslations) as any;
    }
}
export class TranslationsLoader extends BaseTranslationsLoader<TranslationSchema> {
    protected override getFallbackTranslationsMaps(): PerCountryLanguageMap<() => Promise<TranslationSchema>> {
        return {
            be: {
                'fr-BE': async () => (await import('../fallbacks/be/fr-BE/index.ts')).translations,
                'nl-BE': async () => (await import('../fallbacks/be/nl-BE/index.ts')).translations
            },
            ch: {
                'de-CH': async () => (await import('../fallbacks/ch/de-CH/index.ts')).translations,
                'en-CH': async () => (await import('../fallbacks/ch/en-CH/index.ts')).translations,
                'it-CH': async () => (await import('../fallbacks/ch/it-CH/index.ts')).translations,
                'fr-CH': async () => (await import('../fallbacks/ch/fr-CH/index.ts')).translations
            },
            com: {
                'de-DE': async () => (await import('../fallbacks/com/de-DE/index.ts')).translations,
                'en-GB': async () => (await import('../fallbacks/com/en-GB/index.ts')).translations,
                'fr-FR': async () => (await import('../fallbacks/com/fr-FR/index.ts')).translations
            },
            de: {
                'de-DE': async () => (await import('../fallbacks/de/de-DE/index.ts')).translations
            },
            es: {
                'es-ES': async () => (await import('../fallbacks/es/es-ES/index.ts')).translations
            },
            fr: {
                'fr-FR': async () => (await import('../fallbacks/fr/fr-FR/index.ts')).translations
            },
            it: {
                'it-IT': async () => (await import('../fallbacks/it/it-IT/index.ts')).translations
            },
            lu: {
                'fr-BE': async () => (await import('../fallbacks/be/fr-BE/index.ts')).translations
            },
            nl: {
                'nl-NL': async () => (await import('../fallbacks/nl/nl-NL/index.ts')).translations
            },
            pt: { 'pt-PT': async () => (await import('../fallbacks/pt/pt-PT/index.ts')).translations },
            uk: {
                'en-GB': async () => (await import('../fallbacks/uk/en-GB/index.ts')).translations
            }
        };
    }
}
